import {
  parse,
} from "mrz";

/**
 * Pure MRZ-to-vault-fields logic for the passport scanner. The passport's
 * machine-readable zone (the two 44-char OCR-B lines at the bottom of the
 * photo page) carries exactly the fields the travel profile collects, each
 * guarded by check digits — so a misread is detectable instead of silently
 * wrong.
 */

const TD3_LINE_LENGTH = 44;
// OCR reliably collapses the long `<<<<` filler runs (the name line is mostly
// filler), so a real MRZ line can come back well short of 44 chars — re-padding
// with `<`, MRZ's own filler, restores it. 30 keeps ordinary words out: the
// data portions themselves are longer than any word OCR mistakes for MRZ.
const MIN_LINE_LENGTH = 30;
const MRZ_CHARS = /^[A-Z0-9<]+$/;

/**
 * Pull the two TD3 MRZ lines out of raw OCR text. OCR output is noisy — the
 * MRZ lines are the last two lines made only of A–Z, 0–9 and `<` that are
 * roughly the right length.
 */
export function extractMrzLines(text: string): [string, string] | null {
  const candidates = text
    .split("\n")
    .map((line) => line.replace(/\s/g, "").toUpperCase())
    .filter(
      (line) =>
        line.length >= MIN_LINE_LENGTH &&
        line.length <= TD3_LINE_LENGTH + 2 &&
        MRZ_CHARS.test(line),
    )
    .map((line) =>
      line.length > TD3_LINE_LENGTH
        ? line.slice(0, TD3_LINE_LENGTH)
        : line.padEnd(TD3_LINE_LENGTH, "<"),
    );
  const upper = candidates[candidates.length - 2];
  const lower = candidates[candidates.length - 1];
  if (upper === undefined || lower === undefined) return null;
  return [upper, lower];
}

function mrzDateToIso(yymmdd: string, century: number): string | null {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const yy = Number(yymmdd.slice(0, 2));
  const iso = `${century + yy}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== iso
  ) {
    return null;
  }
  return iso;
}

/**
 * MRZ dates are `YYMMDD`. For a birth date the century is unambiguous in
 * practice: a two-digit year after the current one can only be 19xx.
 */
export function mrzBirthDateToIso(
  yymmdd: string,
  today: Date = new Date(),
): string | null {
  const yy = Number(yymmdd.slice(0, 2));
  return mrzDateToIso(yymmdd, yy > today.getFullYear() % 100 ? 1900 : 2000);
}

/**
 * A document expiry is always 20xx: no passport issued last century is still
 * valid, and a still-valid one is what the form is for.
 */
export function mrzExpiryDateToIso(yymmdd: string): string | null {
  return mrzDateToIso(yymmdd, 2000);
}

/**
 * Parse OCR text and map the trustworthy MRZ fields onto vault field keys.
 *
 * Trust is per-field, deliberately: the overall `valid` flag also covers
 * nationality/issuing-country codes and the composite digit, which OCR gets
 * wrong far more often than the fields we use — but the document number and
 * birth date each carry their own check digit, so they are only applied when
 * their own digit passes. Names have no check digit; they ride along only
 * when both anchors validated, which means the two lines were read cleanly.
 * Anything unreadable is simply left for the user to type.
 */
export function mapMrzToVaultValues(
  ocrText: string,
  today: Date = new Date(),
): Record<string, string> | null {
  const lines = extractMrzLines(ocrText);
  if (lines === null) return null;

  let result: ReturnType<typeof parse>;
  try {
    result = parse(lines);
  } catch {
    return null;
  }

  // A field is trustworthy when its format parsed AND its own check digit
  // matched — the library reports those as two separate details.
  const fieldValid = (name: string): boolean =>
    result.details.some((d) => d.field === name && d.valid) &&
    result.details.some((d) => d.field === `${name}CheckDigit` && d.valid);

  const values: Record<string, string> = {};

  if (result.fields.documentNumber && fieldValid("documentNumber")) {
    values["passport_number"] = result.fields.documentNumber;
  }

  const todayIso = today.toISOString().slice(0, 10);
  if (result.fields.birthDate && fieldValid("birthDate")) {
    const iso = mrzBirthDateToIso(result.fields.birthDate, today);
    if (iso !== null && iso <= todayIso) {
      values["date_of_birth"] = iso;
    }
  }

  // The expiry carries its own check digit too. An already-expired document
  // is left blank rather than prefilled: the form refuses a past expiry, and
  // a value the user then has to delete reads as the scanner being wrong.
  if (result.fields.expirationDate && fieldValid("expirationDate")) {
    const iso = mrzExpiryDateToIso(result.fields.expirationDate);
    if (iso !== null && iso >= todayIso) {
      values["passport_expiry"] = iso;
    }
  }

  const anchorsValid = fieldValid("documentNumber") && fieldValid("birthDate");

  // The mrz library already decodes M/F to "male"/"female"; an unspecified
  // marker comes back as neither and is left for the user to pick. Sex has no
  // check digit of its own, so like the names it rides only on both anchors
  // validating — evidence the line was read cleanly.
  if (
    anchorsValid &&
    (result.fields.sex === "male" || result.fields.sex === "female")
  ) {
    values["gender"] = result.fields.sex;
  }
  // Names carry no check digit (ICAO gives line 1 none), so beyond the anchor
  // gate there is one structural tell we can use: the surname/given-names
  // separator is a double filler (`SHEFI<<ITAMAR`), and when OCR drops one
  // `<` the parse collapses to a multi-word "surname" with no first name.
  // Half-filling that as a surname would be confidently wrong — skip both.
  const separatorMisread =
    !result.fields.firstName && (result.fields.lastName ?? "").includes(" ");
  // Names keep the MRZ's uppercase transliteration verbatim: these are legal
  // names headed for airline forms, which expect exactly that — and re-casing
  // invents errors the document doesn't have (MCDONALD -> Mcdonald).
  if (anchorsValid && !separatorMisread) {
    if (result.fields.firstName) {
      values["legal_first_name"] = result.fields.firstName;
    }
    if (result.fields.lastName) {
      values["legal_last_name"] = result.fields.lastName;
    }
  }

  return Object.keys(values).length > 0 ? values : null;
}

const NAME_KEYS = ["legal_first_name", "legal_last_name"] as const;

/**
 * Merge one frame's readings from the two OCR models (generic `eng` and the
 * OCR-B-trained `mrz` model).
 *
 * Check-digit-gated fields union freely — a value only exists when its own
 * digit validated, so whichever model read it, it's right. Names have no
 * check digit and come EXCLUSIVELY from the OCR-B-trained model: measured on
 * an OCR-B specimen, it reads the name line perfectly while the generic
 * model injects confidently-wrong letters — the exact failure names cannot
 * detect. If the trained model read no names, the frame contributes none
 * (another frame, or the user, supplies them); the live path additionally
 * requires cross-frame consensus.
 */
export function mergeModelReadings(
  engValues: Record<string, string>,
  mrzModelValues: Record<string, string>,
): Record<string, string> {
  const merged: Record<string, string> = { ...engValues, ...mrzModelValues };
  for (const key of NAME_KEYS) {
    delete merged[key];
    const trained = mrzModelValues[key];
    if (trained) merged[key] = trained;
  }
  return merged;
}
